import { listenRepeatSentences } from "./listenRepeat.mjs";

/**
 * Speaking exercise types that can carry an uploaded recording of their
 * prompt, played before the candidate answers:
 *
 * - READ: TOEFL Speaking "Listen & Repeat". The candidate hears the sentence,
 *   then repeats it aloud. The sentence text stays the pronunciation-
 *   assessment reference. One exercise per sentence, each with its own file.
 * - QUESTION: TOEIC Speaking "Respond to Questions" style. The candidate hears
 *   and sees the question, then answers it. An optional introduction (TOEIC
 *   Q8-10: a caller introducing themselves) plays before it.
 * - LISTEN_REPEAT: the sentence the candidate repeats, never shown as text.
 *
 * Recording and scoring are the same as without audio.
 */
export const AUDIO_PROMPT_EXERCISE_TYPES = ["READ", "QUESTION", "LISTEN_REPEAT"];

function hasFile(value) {
  return typeof value === "string" && value.trim().length > 0;
}

/**
 * The recordings to play before answering, in order. LISTEN_REPEAT plays the
 * sentence at `sentenceIndex` (always 0 for a single sentence).
 */
export function promptAudioQueue(exerciseType, data, sentenceIndex = 0) {
  if (!AUDIO_PROMPT_EXERCISE_TYPES.includes(exerciseType)) return [];
  if (exerciseType === "LISTEN_REPEAT") {
    const sentence = listenRepeatSentences(data)[sentenceIndex];
    return hasFile(sentence?.audioFileName)
      ? [{ kind: "sentence", src: sentence.audioFileName }]
      : [];
  }
  const queue = [];
  if (exerciseType === "QUESTION" && hasFile(data?.introAudioFileName)) {
    queue.push({ kind: "intro", src: data.introAudioFileName });
  }
  if (hasFile(data?.audioFileName)) {
    queue.push({ kind: "prompt", src: data.audioFileName });
  }
  return queue;
}

export function hasAudioPrompt(exerciseType, data, sentenceIndex = 0) {
  return promptAudioQueue(exerciseType, data, sentenceIndex).length > 0;
}

/**
 * Per-exercise playback settings of the prompt recordings. `maxPlays` 0 means
 * unlimited, as before the setting existed.
 */
export function promptPlaybackSettings(data) {
  const plays = Number.parseInt(data?.audioMaxPlays, 10);
  return {
    maxPlays: Number.isFinite(plays) && plays > 0 ? plays : 0,
    autoplay: data?.audioAutoplay === true,
  };
}

/**
 * Whether a Listen & Repeat sentence is hidden from the candidate, so they
 * repeat what they heard rather than read it. Only honored when there is a
 * recording to listen to: a READ exercise with neither text nor audio would
 * leave nothing to say.
 */
export function isListenRepeatTextHidden(exerciseType, data) {
  return (
    exerciseType === "READ" &&
    hasAudioPrompt(exerciseType, data) &&
    data?.hideText === true
  );
}

/**
 * The preparation countdown stands still until the recording has played to
 * the end (or failed to load), so preparation time starts once the candidate
 * has heard the prompt. `audioDone` latches: replaying the prompt later does
 * not stop the countdown again.
 */
export function shouldHoldPrepareTimer(exerciseType, data, audioDone, sentenceIndex = 0) {
  return hasAudioPrompt(exerciseType, data, sentenceIndex) && !audioDone;
}

